/** Auth-контекст и хук для текущего пользователя.
 *
 *  Подгружает /api/identity/me/ при mount через TanStack Query. Использует
 *  наш существующий apiRequest (Bearer-токен из sessionStorage). Login через
 *  /auth/token/login/, logout через /auth/token/logout/.
 *
 *  Реэкспорт функций сессии из ~/state/session оставлен (используется LoginPage).
 */
import { createContext, createEffect, useContext, type ParentComponent } from 'solid-js'
import { createQuery, useQueryClient } from '@tanstack/solid-query'
import { apiRequest } from '~/lib/apiRequest'
import {
  clearSession,
  getToken,
  isAuthenticated,
  setToken,
} from '~/state/session'
import type { MeResponse } from '~/types'
import { useI18n, type Locale } from '~/i18n'

interface TokenLoginResponse {
  auth_token: string
}

interface AuthContextValue {
  meQuery: ReturnType<typeof useMeQuery>
  login: (email: string, password: string) => Promise<MeResponse>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>()

function useMeQuery() {
  return createQuery(() => ({
    queryKey: ['me'],
    queryFn: async (): Promise<MeResponse | null> => {
      if (!getToken()) return null
      try {
        return await apiRequest<MeResponse>('GET', 'api/identity/me/')
      } catch {
        // Битый токен или сеть недоступна — считаем не залогинены и чистим.
        clearSession()
        return null
      }
    },
    retry: false,
    staleTime: 1000 * 60,
  }))
}

export const AuthProvider: ParentComponent = (props) => {
  const queryClient = useQueryClient()
  const meQuery = useMeQuery()
  const i18n = useI18n()

  // После успешного логина (и при последующих рефетчах /me/) подхватываем
  // сохранённый в профиле язык. Если поле пустое — оставляем то, что выбрал
  // пользователь в этом браузере (localStorage).
  createEffect(() => {
    const pref = meQuery.data?.user?.preferred_language
    if (pref === 'ru' || pref === 'en') {
      if (pref !== i18n.locale()) {
        i18n.setLocale(pref as Locale)
      }
    }
  })

  const login = async (email: string, password: string): Promise<MeResponse> => {
    const res = await apiRequest<TokenLoginResponse>(
      'POST',
      'auth/token/login/',
      { email, password },
      { skipAuth: true },
    )
    if (!res?.auth_token) {
      throw new Error('auth_token не получен')
    }
    setToken(res.auth_token)
    const me = await apiRequest<MeResponse>('GET', 'api/identity/me/')
    queryClient.setQueryData(['me'], me)
    return me
  }

  const logout = async () => {
    try {
      await apiRequest('POST', 'auth/token/logout/')
    } catch {
      // Игнорируем — главное локально вычистить.
    }
    clearSession()
    queryClient.setQueryData(['me'], null)
    await queryClient.invalidateQueries()
  }

  return (
    <AuthContext.Provider value={{ meQuery, login, logout }}>
      {props.children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export { isAuthenticated }
